/**
 * 서버 업스트림 호출 소요시간 로그 — **dev(Cloud Run)에서만 켜진다.**
 *
 * ## 왜 있나
 *
 * 2026-10-06 dev 전체 E2E 중 SSR 요청 몇 건이 일제히 ~51초 걸렸는데(정적 파일·prefetch 는 같은
 * 순간에 정상), Cloud Run 요청 로그만으로는 **SSR 안에서 어느 업스트림을 기다렸나**를 가를 수
 * 없었다 — 앱 로그엔 소요시간도 trace 도 없다. 이 모듈은 호출 지점(`withUpstreamTiming`)마다
 * 시작·종료·에러 코드를 한 줄씩 남기고, 노드 쪽 수집기(`upstream-timing-node.ts`)가 그 호출
 * 안에서 나간 HTTP 요청의 단계(보내기까지 · 응답 헤더까지 · 본문)를 같은 종료 줄에 붙인다.
 *
 * ## 켜짐 / 꺼짐
 *
 * 판정은 서버 기동 시 `src/instrumentation.ts` 가 한 번 한다(`isUpstreamTimingEnabled`). 켜지면 그
 * 상태를 `globalThis` 에 두고, 이 파일은 그 값만 본다. 그래서
 *
 * - 이 파일은 node 모듈을 import 하지 않는다 — `BannerService` 처럼 브라우저에서도 도는 코드가
 *   불러도 된다.
 * - 꺼져 있으면(prod·로컬·브라우저·jest) `fn()` 을 **그대로** 부른다 — 동작도 타이밍도 그대로다.
 * - `globalThis` 에 두는 이유: instrumentation 과 페이지는 번들 청크가 달라 모듈 인스턴스가
 *   갈릴 수 있다. 상태가 둘이 되면 수집기가 호출 지점의 컨텍스트를 못 본다.
 *
 * ## 로그 읽는 법 (Cloud Logging, ⚠️ `--project designer-v2-dev`)
 *
 * - 느린 호출: `jsonPayload.tag="upstream-timing" AND jsonPayload.ms>=3000`
 * - 한 SSR 요청의 업스트림 전부: 요청 로그와 **trace 로 묶인다**(`logging.googleapis.com/trace`).
 * - HTTP 단계: `jsonPayload.http[]` — `waitMs`(보내기까지: 커넥션 대기·연결) · `connectMs`(새 소켓일 때
 *   연결까지) · `ttfbMs`(보낸 뒤 응답 헤더까지) · `bodyMs` · `sock`/`reuse`(몇 번째 소켓의 몇 번째
 *   요청인지) · `rl`/`ttl`(응답의 `X-Rl`·`X-Ttl` — ip-api 레이트리밋 잔량·리셋까지 초).
 *
 * ⚠️ IP·이메일·쿼리스트링·에러 메시지는 남기지 않는다(docs/pitfalls.md 「console.log(order) · 결제
 * 로깅」). ip-api 는 path 에 IP 가 들어 있어 `redactPath` 가 지운다.
 */

import { isProd } from "@/Env";

export const UPSTREAM_TIMING_TAG = "upstream-timing";

export type EnvLike = Record<string, string | undefined>;

/**
 * dev(Cloud Run)인가 — Cloud Run 이 서비스 컨테이너에 넣는 `K_SERVICE` 로 본다. prod(Vercel)·로컬·jest 엔 없고
 * 요청으로는 못 바꾼다. dev 판정이 필요한 곳(이 타이밍 로그 · 지오 클라이언트 IP `src/libs/geo/client-ip.ts`)이
 * 이것 하나를 쓴다 — 판정이 갈라지면 한쪽만 dev 로 도는 상태가 된다.
 */
export const isCloudRun = (env: EnvLike = process.env): boolean =>
  !!env.K_SERVICE;

/**
 * 켤지 판정한다 — dev(Cloud Run)에서만. `src/instrumentation.ts` 가 **노드 수집기를 불러오기 전에**
 * 부른다(prod 는 수집기 모듈을 아예 싣지 않는다).
 *
 * - dev 는 `NEXT_PUBLIC_ENV` 가 **비어 있다**(로그 `env: undefined`) — 그 값으로는 dev 를 못 고른다.
 *   대신 `isCloudRun`(`K_SERVICE`)을 본다.
 * - prod 빌드(`isProd`)면 무엇이 와도 끈다.
 * - `UPSTREAM_TIMING_LOG=0` 은 dev 에서도 끈다(배포 없이 서비스 env 로) · `=1` 은 로컬 검증용으로 켠다.
 */
export function isUpstreamTimingEnabled({
  env = process.env,
  prod = isProd,
}: { env?: EnvLike; prod?: boolean } = {}): boolean {
  if (prod) return false;
  if (env.UPSTREAM_TIMING_LOG === "0") return false;
  if (env.UPSTREAM_TIMING_LOG === "1") return true;
  return isCloudRun(env);
}

/** 이 이상 걸린 호출은 `WARNING` 으로 남긴다 — `severity>=WARNING` 하나로 느린 것만 걸러 보게. */
export const UPSTREAM_SLOW_MS = 3000;

type Field = string | number | boolean | undefined;
export type TimingFields = Record<string, Field>;

/** 들어온 SSR 요청 단위 컨텍스트 — 노드 수집기가 요청마다 채운다. */
export interface RequestTimingContext {
  /** Cloud Trace id(32 hex). 요청 로그와 앱 로그를 묶는 키. */
  trace?: string;
  /** 들어온 요청 경로(쿼리 제거·식별자 가림). */
  reqPath?: string;
}

/** 호출 지점 안에서 나간 HTTP 요청 하나의 단계 요약. 노드 수집기가 채운다. */
export interface HttpPhase {
  origin: string;
  path: string;
  status?: number;
  /** 실패 코드(`ECONNRESET`·`AbortError` 등). 메시지는 남기지 않는다. */
  error?: string;
  /** 소켓 일련번호 — 같은 번호면 같은 TCP 연결이다. */
  sock?: number;
  /** 그 소켓에서 몇 번째 요청인지. 1 이면 새 연결, 2 이상이면 keep-alive 재사용. */
  reuse?: number;
  connectMs?: number;
  waitMs?: number;
  ttfbMs?: number;
  bodyMs?: number;
  totalMs?: number;
  /** 응답 `X-Rl` — ip-api 의 현재 창 잔여 요청 수. */
  rl?: string;
  /** 응답 `X-Ttl` — ip-api 레이트리밋 창이 리셋되기까지 초. */
  ttl?: string;
  /** 종료 줄을 쓸 때까지 끝나지 않았다(본문 미소비 등). */
  pending?: boolean;
}

export interface UpstreamCallContext extends RequestTimingContext {
  label: string;
  http: HttpPhase[];
  ended: boolean;
}

export type TimingStore = RequestTimingContext | UpstreamCallContext;

export interface UpstreamTimingState {
  storage: {
    getStore(): TimingStore | undefined;
    run<R>(store: TimingStore, fn: () => R): R;
  };
  /** Cloud Logging trace 접두사(`projects/<id>/traces/`). 없으면 trace 를 싣지 않는다. */
  traceProject?: string;
  /** 한 줄 출력. 기본 `console.log` — Cloud Run 은 한 줄 JSON 을 `jsonPayload` 로 받는다. */
  write?: (line: string) => void;
}

const STATE_KEY = Symbol.for("bhaptics.upstream-timing");

type StateHolder = Record<symbol, UpstreamTimingState | undefined>;

export function getUpstreamTimingState(): UpstreamTimingState | undefined {
  return (globalThis as unknown as StateHolder)[STATE_KEY];
}

export function setUpstreamTimingState(
  state: UpstreamTimingState | undefined,
): void {
  (globalThis as unknown as StateHolder)[STATE_KEY] = state;
}

export function isUpstreamCallContext(
  store: TimingStore | undefined,
): store is UpstreamCallContext {
  return !!store && "label" in store && Array.isArray(store.http);
}

const REDACTED = ":x";
const MAX_PATH_LENGTH = 160;

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * 식별자일 수 있는 path 조각 — IP(v4·v6) · 이메일 · 긴 숫자 · 긴 hex(ObjectId·UUID) · 토큰 길이.
 * 슬러그(`tactsuit-pro`)·빌드 id 는 공개값이라 남긴다.
 */
function isSensitiveSegment(segment: string): boolean {
  if (!segment) return false;
  const s = safeDecode(segment);
  return (
    s.includes("@") ||
    s.includes(":") ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(s) ||
    /^\d{4,}$/.test(s) ||
    /^[0-9a-f-]{16,}$/i.test(s) ||
    s.length > 40
  );
}

/** 쿼리·해시를 떼고, 식별자일 수 있는 조각을 `:x` 로 가린다. */
export function redactPath(raw: string): string {
  const path = raw.split(/[?#]/, 1)[0] ?? "";
  return path
    .split("/")
    .map((segment) => (isSensitiveSegment(segment) ? REDACTED : segment))
    .join("/")
    .slice(0, MAX_PATH_LENGTH);
}

function errorFields(error: unknown): TimingFields {
  const e = error as
    | {
        name?: unknown;
        code?: unknown;
        cause?: { code?: unknown; name?: unknown };
      }
    | undefined;
  const pick = (v: unknown) => (typeof v === "string" ? v : undefined);
  return {
    errorName: pick(e?.name),
    errorCode: pick(e?.code) ?? pick(e?.cause?.code) ?? pick(e?.cause?.name),
  };
}

function writeLine(
  state: UpstreamTimingState,
  ctx: UpstreamCallContext,
  severity: "DEBUG" | "INFO" | "WARNING",
  fields: TimingFields & { event: string },
  http?: HttpPhase[],
): void {
  try {
    const summary = [
      `[${UPSTREAM_TIMING_TAG}]`,
      ctx.label,
      fields.event,
      fields.outcome,
      typeof fields.ms === "number" ? `${fields.ms}ms` : undefined,
    ]
      .filter(Boolean)
      .join(" ");
    // 호출 지점 값(`fields`)을 **먼저** 펼친다 — meta·describe 가 우연히 같은 이름을 써도
    // 예약 키(severity·label·req…)를 덮지 못하게.
    const entry = {
      ...fields,
      severity,
      message: summary,
      tag: UPSTREAM_TIMING_TAG,
      label: ctx.label,
      req: ctx.reqPath,
      ...(http ? { http } : {}),
      ...(ctx.trace && state.traceProject
        ? {
            "logging.googleapis.com/trace": `${state.traceProject}${ctx.trace}`,
          }
        : {}),
    };
    (state.write ?? console.log)(JSON.stringify(entry));
  } catch {
    // 진단 로그가 업무 흐름을 깨면 안 된다.
  }
}

/**
 * 호출이 끝난 **뒤에** 끝난 HTTP 요청(본문을 늦게 읽은 경우 등)을 따로 한 줄 남긴다.
 * 노드 수집기 전용.
 */
export function writeLateHttpPhase(
  state: UpstreamTimingState,
  ctx: UpstreamCallContext,
  phase: HttpPhase,
): void {
  writeLine(
    state,
    ctx,
    (phase.totalMs ?? 0) >= UPSTREAM_SLOW_MS || phase.error
      ? "WARNING"
      : "INFO",
    { event: "late-http" },
    [phase],
  );
}

function elapsedMs(startedAt: number): number {
  return Math.round(performance.now() - startedAt);
}

/**
 * `fn` 을 업스트림 호출 하나로 재서 시작·종료 줄을 남긴다. 꺼져 있으면 `fn()` 그대로다.
 *
 * 결과·예외는 손대지 않고 그대로 돌려준다(던지면 그대로 던진다).
 *
 * @param label 호출 지점 이름(`ip-api`·`shipping-countries` …). 로그 검색 키.
 * @param options.meta 시작·종료 줄에 함께 남길 값. ⚠️ 개인정보 금지.
 * @param options.describe 성공 결과에서 남길 값을 고른다. ⚠️ 개인정보 금지.
 */
export async function withUpstreamTiming<T>(
  label: string,
  fn: () => Promise<T>,
  options: {
    meta?: TimingFields;
    describe?: (result: T) => TimingFields;
  } = {},
): Promise<T> {
  const state = getUpstreamTimingState();
  if (!state) return fn();

  const parent = state.storage.getStore();
  const ctx: UpstreamCallContext = {
    trace: parent?.trace,
    reqPath: parent?.reqPath,
    label,
    http: [],
    ended: false,
  };
  const startedAt = performance.now();
  // 아래 줄들 모두 호출 지점 값(meta·describe)을 먼저 펼치고 event·outcome·ms 를 뒤에 둔다 — 덮이지 않게.
  writeLine(state, ctx, "DEBUG", { ...options.meta, event: "start" });

  let result: T;
  try {
    result = await state.storage.run(ctx, fn);
  } catch (error) {
    ctx.ended = true;
    writeLine(
      state,
      ctx,
      "WARNING",
      {
        ...options.meta,
        ...errorFields(error),
        event: "end",
        outcome: "error",
        ms: elapsedMs(startedAt),
      },
      ctx.http,
    );
    throw error;
  }

  ctx.ended = true;
  const ms = elapsedMs(startedAt);
  let described: TimingFields = {};
  try {
    described = options.describe?.(result) ?? {};
  } catch {
    // describe 실패는 무시한다 — 결과는 그대로 돌려준다.
  }
  writeLine(
    state,
    ctx,
    ms >= UPSTREAM_SLOW_MS ? "WARNING" : "INFO",
    { ...options.meta, ...described, event: "end", outcome: "ok", ms },
    ctx.http,
  );
  return result;
}
